import axios, { AxiosError } from "axios";
import { env, APP_URLS, type AppKey } from "@astro/config";
import { ApiRequestError } from "@astro/api";
import type {
  ApiError,
  ChangePasswordPayload,
  ChangePasswordResult,
  LoginCredentials,
  RegisterPayload,
  RegisterResult,
  Session,
} from "@astro/types";

/**
 * Thin axios client over the standalone auth BFF (auth.astrorajumaharaj.com).
 * The BFF exchanges credentials for the Laravel Sanctum token and seals it in
 * an httpOnly parent-domain cookie; the browser never sees an access token.
 */
const bff = axios.create({
  baseURL: env.VITE_AUTH_BASE_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json", Accept: "application/json" },
});

interface UpstreamErrorBody extends Partial<ApiError> {
  errors?: Record<string, string[]>;
}

bff.interceptors.response.use(undefined, (error: AxiosError) => {
  const body = (error.response?.data ?? {}) as UpstreamErrorBody;
  throw new ApiRequestError({
    code: body.code ?? (error.response ? "http_error" : "network_error"),
    message: body.message ?? error.message,
    status: error.response?.status ?? 0,
    details: body.details ?? body.errors,
  });
});

/** Central login page — prefer the current app's /login route when available, otherwise use an override or the website fallback. */
function loginPageUrl(): string {
  if (env.VITE_LOGIN_URL) return env.VITE_LOGIN_URL;
  if (typeof window !== "undefined") return new URL("/login", window.location.origin).toString();
  return `${APP_URLS.website}/login`;
}

export const authClient = {
  /** Silent SSO: returns the current session if the shared cookie is valid. */
  async session(): Promise<Session | null> {
    try {
      const res = await bff.get<Session>("/session");
      return res.data;
    } catch (err) {
      if (err instanceof ApiRequestError && err.error.status === 401) return null;
      throw err;
    }
  },

  /** Email + password login. On success the BFF sets the session cookie. */
  async loginWithPassword(credentials: LoginCredentials): Promise<Session> {
    const res = await bff.post<Session>("/login", credentials);
    return res.data;
  },

  /** WhatsApp OTP login: exchange a verified mobile+otp for a session. On success the BFF seals the token into the session cookie. */
  async loginWithOtp(mobile: string, otp: string): Promise<Session> {
    const res = await bff.post<Session>("/login/otp/verify", { mobile, otp });
    return res.data;
  },

  /** Create an account; auto-logs-in when the API returns a token. */
  async register(payload: RegisterPayload): Promise<RegisterResult> {
    const res = await bff.post<RegisterResult>("/register", payload);
    return res.data;
  },

  /**
   * Change the signed-in user's password. Upstream signs out every other
   * device; the BFF re-seals this browser's cookie with the replacement token
   * so the current session survives the change.
   */
  async changePassword(payload: ChangePasswordPayload): Promise<ChangePasswordResult> {
    const res = await bff.post<ChangePasswordResult>("/change-password", payload);
    return res.data;
  },

  /** Re-validate the sealed token; true if the session is still alive. */
  async refresh(): Promise<boolean> {
    try {
      await bff.post("/refresh");
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Redirect into the central login page, returning to `returnTo` afterwards.
   * `replace: true` swaps the current history entry instead of pushing a new
   * one — used for guard-triggered redirects (e.g. RequireAuth) so the back
   * button skips the guarded page instead of bouncing right back onto it.
   * Manual login clicks keep the default (push) so cancelling returns here.
   */
  login(returnTo: string = window.location.href, opts: { replace?: boolean } = {}): void {
    const url = new URL(loginPageUrl());
    url.searchParams.set("redirect", returnTo);
    if (opts.replace) {
      window.location.replace(url.toString());
    } else {
      window.location.assign(url.toString());
    }
  },

  /** Global (single) logout across all subdomains — clears the shared cookie. */
  logout(returnApp: AppKey = "website"): void {
    const url = new URL(`${env.VITE_AUTH_BASE_URL}/logout`);
    url.searchParams.set("app", returnApp);
    url.searchParams.set("redirect", window.location.origin);
    window.location.assign(url.toString());
  },
};
